/**
 * The widget's stylesheet: one <style id="amai-chat-styles"> in the host
 * page's <head>, shared by the floating bubble (.amai-chat-root) and the
 * chat windows placed in the page (.amai-chat-embed). Every rule is
 * prefixed, so it neither reaches the host page nor depends on it.
 */
export function injectStyles(primaryColor: string): void {
    if (document.getElementById("amai-chat-styles")) return;
    const style = document.createElement("style");
    style.id = "amai-chat-styles";
    style.textContent = `
.amai-chat-root, .amai-chat-embed {
    --amai-primary: ${escapeCss(primaryColor)};
    --amai-bg: #ffffff;
    --amai-text: #1c2024;
    --amai-muted: #6b7280;
    --amai-border: #e5e7eb;
    --amai-bot-bg: #f3f4f6;
    --amai-visitor-bg: var(--amai-primary);
    --amai-visitor-text: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: var(--amai-text);
    box-sizing: border-box;
}
.amai-chat-root *, .amai-chat-root *::before, .amai-chat-root *::after,
.amai-chat-embed *, .amai-chat-embed *::before, .amai-chat-embed *::after {
    box-sizing: border-box;
}
.amai-chat-launcher {
    position: fixed;
    right: 20px;
    bottom: 20px;
    width: 56px;
    height: 56px;
    border-radius: 28px;
    background: var(--amai-primary);
    color: #fff;
    border: none;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2147483646;
    transition: transform 120ms ease, box-shadow 120ms ease;
}
.amai-chat-launcher:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
}
.amai-chat-launcher-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    display: block;
    pointer-events: none;
}
/* Teaser bubble: right above the launcher, so it stays clear of
 * whatever a site keeps along its bottom-left. Mounted invisible (and
 * so not clickable); it only shows while nothing the site keeps on
 * screen sits under it, such as a cookie banner (showTeaser). */
.amai-chat-teaser {
    position: fixed;
    right: 20px;
    bottom: 88px;
    max-width: 280px;
    display: flex;
    align-items: flex-start;
    background: var(--amai-bg);
    color: var(--amai-text);
    border: 1px solid var(--amai-border);
    border-radius: 16px 16px 4px 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
    z-index: 2147483646;
    visibility: hidden;
}
.amai-chat-teaser-visible {
    visibility: visible;
    animation: amai-teaser-in 200ms ease-out;
}
.amai-chat-teaser-body {
    flex: 1 1 auto;
    min-width: 0;
    background: transparent;
    border: none;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
    padding: 10px 2px 10px 14px;
}
.amai-chat-teaser-text {
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
}
.amai-chat-teaser-close {
    flex: 0 0 auto;
    background: transparent;
    border: none;
    color: var(--amai-muted);
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    padding: 9px 10px 8px 6px;
    border-radius: 4px;
}
.amai-chat-teaser-close:hover { color: var(--amai-text); }
@keyframes amai-teaser-in {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
}
@media (max-width: 480px) {
    .amai-chat-teaser {
        bottom: 84px;
        max-width: 240px;
        font-size: 13px;
    }
    .amai-chat-teaser-body { padding: 8px 2px 8px 12px; }
    .amai-chat-teaser-text { -webkit-line-clamp: 3; }
}
@media (prefers-reduced-motion: reduce) {
    .amai-chat-teaser-visible { animation: none; }
}
.amai-chat-popover {
    position: fixed;
    right: 20px;
    bottom: 88px;
    width: 380px;
    max-width: calc(100vw - 32px);
    height: 560px;
    max-height: calc(100vh - 120px);
    background: var(--amai-bg);
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
    display: none;
    flex-direction: column;
    overflow: hidden;
    z-index: 2147483647;
    border: 1px solid var(--amai-border);
}
.amai-chat-popover-open { display: flex; }
@media (max-width: 480px) {
    .amai-chat-popover {
        right: 0; left: 0; bottom: 0; top: 0;
        width: 100vw; height: 100vh;
        max-width: 100vw; max-height: 100vh;
        border-radius: 0;
        border: none;
    }
}
.amai-chat-header {
    background: var(--amai-primary);
    color: #fff;
    padding: 10px 12px 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}
.amai-chat-header-id {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.amai-chat-header-avatar {
    flex: 0 0 auto;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: #fff;
}
.amai-chat-header-avatar > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
/* The built-in glyph is drawn at 28px for the launcher; scale it down
 * to sit inside the smaller header circle. */
.amai-chat-header-avatar > svg {
    width: 19px;
    height: 19px;
}
.amai-chat-header-text { min-width: 0; }
.amai-chat-header-title {
    font-weight: 600;
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.amai-chat-header-subtitle {
    font-size: 11.5px;
    line-height: 1.3;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.82);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.amai-chat-close {
    background: transparent;
    border: none;
    color: #fff;
    font-size: 22px;
    cursor: pointer;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 4px;
}
.amai-chat-close:hover { background: rgba(255,255,255,0.15); }
.amai-chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.amai-chat-bubble {
    max-width: 80%;
    padding: 10px 14px;
    border-radius: 16px;
    word-wrap: break-word;
    white-space: pre-wrap;
}
.amai-chat-bubble-visitor {
    align-self: flex-end;
    background: var(--amai-visitor-bg);
    color: var(--amai-visitor-text);
    border-bottom-right-radius: 4px;
}
.amai-chat-bubble-bot, .amai-chat-bubble-agent, .amai-chat-bubble-system {
    align-self: flex-start;
    background: var(--amai-bot-bg);
    color: var(--amai-text);
    border-bottom-left-radius: 4px;
}
.amai-chat-bubble a {
    color: inherit;
    text-decoration: underline;
    word-break: break-word;
}
.amai-chat-bubble-visitor a { color: #ffffff; }
.amai-chat-bubble-bot a, .amai-chat-bubble-agent a, .amai-chat-bubble-system a {
    color: var(--amai-primary);
}
.amai-chat-typing { padding: 14px 16px; }
.amai-chat-typing span {
    display: inline-block;
    width: 6px; height: 6px;
    margin-right: 3px;
    background: var(--amai-muted);
    border-radius: 50%;
    animation: amai-bounce 1.2s infinite ease-in-out;
}
.amai-chat-typing span:nth-child(2) { animation-delay: 0.15s; }
.amai-chat-typing span:nth-child(3) { animation-delay: 0.3s; }
@keyframes amai-bounce {
    0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
    40%           { opacity: 1;   transform: translateY(-4px); }
}
.amai-chat-options {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 0 16px 8px;
}
.amai-chat-options:empty { display: none; }
.amai-chat-inline-options {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-self: flex-start;
    max-width: 90%;
}
.amai-chat-inline-options button[disabled] {
    opacity: 0.55;
    cursor: default;
}
.amai-chat-inline-multi {
    align-self: flex-start;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 90%;
}
.amai-chat-inline-multi-actions {
    display: flex;
    justify-content: flex-end;
}
/* Selected toggle chip: filled with the primary color. Specificity
 * forced higher than .amai-chat-option so the visual state can't be
 * swallowed by host-page CSS or rule ordering. */
.amai-chat-root .amai-chat-option-toggle.amai-chat-option-selected,
.amai-chat-root .amai-chat-option-toggle[aria-pressed="true"],
.amai-chat-embed .amai-chat-option-toggle.amai-chat-option-selected,
.amai-chat-embed .amai-chat-option-toggle[aria-pressed="true"] {
    background: var(--amai-primary);
    color: #fff;
    box-shadow: 0 0 0 1px var(--amai-primary) inset;
}
.amai-chat-multi-submit {
    background: var(--amai-primary);
    color: #fff;
    border: none;
    padding: 8px 16px;
    border-radius: 18px;
    cursor: pointer;
    font: inherit;
    font-weight: 600;
    line-height: 1.2;
    min-width: 96px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.amai-chat-multi-submit:hover:not([disabled]) {
    filter: brightness(1.05);
}
.amai-chat-multi-submit[disabled] {
    background: var(--amai-bot-bg);
    color: var(--amai-muted);
    cursor: not-allowed;
    box-shadow: none;
}
.amai-chat-root .amai-chat-inline-input,
.amai-chat-embed .amai-chat-inline-input {
    align-self: stretch;
    display: flex;
    gap: 6px;
    width: 100%;
    box-sizing: border-box;
}
.amai-chat-inline-input-field {
    flex: 1;
    min-width: 0;
    border: 1px solid var(--amai-border);
    border-radius: 24px;
    padding: 13px 16px;
    font: inherit;
    color: var(--amai-text);
    outline: none;
    background: #fff;
}
.amai-chat-inline-input-field:focus {
    border-color: var(--amai-primary);
}
.amai-chat-inline-input-field:disabled {
    opacity: 0.6;
    background: var(--amai-bot-bg);
}
.amai-chat-inline-input-send {
    background: var(--amai-primary);
    color: #fff;
    border: none;
    width: 48px;
    height: 48px;
    border-radius: 24px;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
}
.amai-chat-inline-input-send:disabled {
    opacity: 0.55;
    cursor: default;
}
.amai-chat-inline-input-skip {
    background: transparent;
    border: 1px solid var(--amai-border);
    color: var(--amai-muted);
    padding: 0 14px;
    height: 48px;
    border-radius: 24px;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    line-height: 1;
    white-space: nowrap;
}
.amai-chat-inline-input-skip:hover:not([disabled]) {
    background: var(--amai-bot-bg);
    color: var(--amai-text);
}
.amai-chat-inline-input-skip[disabled] {
    opacity: 0.45;
    cursor: default;
}
.amai-chat-property-links {
    align-self: flex-start;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    max-width: 90%;
}
.amai-chat-property-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid var(--amai-primary);
    color: var(--amai-primary);
    padding: 6px 12px;
    border-radius: 16px;
    font-size: 13px;
    line-height: 1.3;
    text-decoration: none;
    cursor: pointer;
    transition: background 100ms ease, color 100ms ease;
}
.amai-chat-property-link::after {
    content: "↗";
    font-size: 12px;
    opacity: 0.7;
}
.amai-chat-property-link:hover {
    background: var(--amai-primary);
    color: #fff;
}
.amai-chat-property-cards {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    box-sizing: border-box;
}
.amai-chat-property-card {
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid var(--amai-border);
    border-radius: 12px;
    overflow: hidden;
    text-decoration: none;
    color: inherit;
    transition: border-color 100ms ease, box-shadow 100ms ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.amai-chat-property-card:hover {
    border-color: var(--amai-primary);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
.amai-chat-property-card-thumbs {
    display: flex;
    gap: 6px;
    padding: 10px 12px 0;
}
.amai-chat-property-card-thumb {
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    height: 54px;
    background: var(--amai-bot-bg);
    border: 1px solid var(--amai-border);
    border-radius: 8px;
    overflow: hidden;
    cursor: zoom-in;
    padding: 0;
    display: block;
    transition: border-color 100ms ease, transform 100ms ease;
}
.amai-chat-property-card-thumb:hover {
    border-color: var(--amai-primary);
    transform: translateY(-1px);
}
.amai-chat-property-card-thumb > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    user-select: none;
    -webkit-user-drag: none;
}
.amai-chat-property-card-thumb-more::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(15, 17, 21, 0.55);
    z-index: 1;
}
.amai-chat-property-card-thumb-more-label {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 600;
    font-size: 13px;
    z-index: 2;
}
.amai-chat-gallery {
    position: absolute;
    inset: 0;
    background: rgba(15, 17, 21, 0.96);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    z-index: 10;
    user-select: none;
}
.amai-chat-gallery-image-wrap {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 16px;
}
.amai-chat-gallery-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 4px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}
.amai-chat-gallery-close,
.amai-chat-gallery-prev,
.amai-chat-gallery-next {
    position: absolute;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    border: none;
    cursor: pointer;
    font: inherit;
    border-radius: 50%;
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
}
.amai-chat-gallery-close:hover,
.amai-chat-gallery-prev:hover,
.amai-chat-gallery-next:hover {
    background: rgba(255, 255, 255, 0.22);
}
.amai-chat-gallery-close {
    top: 12px;
    right: 12px;
    font-size: 22px;
}
.amai-chat-gallery-prev {
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 24px;
}
.amai-chat-gallery-next {
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 24px;
}
.amai-chat-gallery-counter {
    color: rgba(255, 255, 255, 0.7);
    text-align: center;
    font-size: 12px;
    padding: 8px;
    letter-spacing: 0.04em;
}
.amai-chat-property-card-body {
    padding: 10px 12px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.amai-chat-property-card-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--amai-text);
    line-height: 1.3;
    word-break: break-word;
}
.amai-chat-property-card-meta {
    font-size: 12px;
    color: var(--amai-text);
    line-height: 1.3;
}
.amai-chat-property-card-location {
    font-size: 12px;
    color: var(--amai-muted);
    line-height: 1.3;
}
.amai-chat-property-card-cta {
    margin-top: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--amai-primary);
}
.amai-chat-place-suggestions {
    list-style: none;
    margin: 4px 0 0;
    padding: 4px 0;
    background: #fff;
    border: 1px solid var(--amai-border);
    border-radius: 8px;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    max-height: 220px;
    overflow-y: auto;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1;
    display: none;
}
.amai-chat-place-suggestions-open { display: block; }
.amai-chat-place-suggestion {
    padding: 8px 12px;
    cursor: pointer;
    font-size: 13px;
    line-height: 1.3;
    color: var(--amai-text);
}
.amai-chat-place-suggestion:hover,
.amai-chat-place-suggestion-active {
    background: var(--amai-bot-bg);
}
.amai-chat-place-suggestion-active {
    background: var(--amai-primary);
    color: #fff;
}
.amai-chat-option {
    background: #fff;
    border: 1px solid var(--amai-primary);
    color: var(--amai-primary);
    padding: 6px 12px;
    border-radius: 16px;
    cursor: pointer;
    font-size: 13px;
    line-height: 1.3;
}
.amai-chat-option:hover {
    background: var(--amai-primary);
    color: #fff;
}
.amai-chat-input {
    display: flex;
    align-items: center;
    border-top: 1px solid var(--amai-border);
    padding: 10px;
    gap: 8px;
}
.amai-chat-input-field {
    flex: 1;
    border: 1px solid var(--amai-border);
    border-radius: 24px;
    padding: 13px 16px;
    font: inherit;
    color: var(--amai-text);
    outline: none;
}
.amai-chat-input-field:focus { border-color: var(--amai-primary); }
.amai-chat-send {
    background: var(--amai-primary);
    color: #fff;
    border: none;
    width: 48px;
    height: 48px;
    border-radius: 24px;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    transition: filter 100ms ease, transform 100ms ease;
}
.amai-chat-send:hover { filter: brightness(1.05); transform: scale(1.04); }
.amai-chat-send:active { transform: scale(0.96); }
/* The plane points right and its tail is the widest part, so measured
 * centring leaves it looking shunted left. Nudge it back. */
.amai-chat-send > svg, .amai-chat-inline-input-send > svg {
    transform: translateX(1px);
}
.amai-chat-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 12px 8px;
    background: var(--amai-bg);
}
.amai-chat-footer-credit {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 10.5px;
    line-height: 1;
    color: var(--amai-muted);
    text-decoration: none;
    opacity: 0.75;
    transition: opacity 100ms ease;
}
.amai-chat-footer-credit:hover { opacity: 1; }
.amai-chat-footer-name {
    font-weight: 600;
    color: var(--amai-text);
}
.amai-chat-footer-credit:hover .amai-chat-footer-name {
    text-decoration: underline;
}
/* For screen readers only: the input's label. */
.amai-chat-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
/* A chat window placed in the page (embed.ts): in the flow of the page,
 * in the page's font. The page sets its size on the container: the
 * height through --amai-chat-height (600px, 70vh on phones), the width
 * through --amai-chat-width (the container's); a container with a
 * height of its own caps the height, and the window is never wider than
 * the container. A narrower one sits where --amai-chat-align says
 * (below). The messages scroll inside. Until the visitor's chat begins
 * it is compact (below). Positioned only so the photo gallery can cover
 * it. overflow-anchor: the browser must not hold the page still by a
 * message in here. With the window's top above the screen it would
 * pick one, and every message that comes in would scroll the page. */
.amai-chat-embed {
    position: relative;
    display: flex;
    flex-direction: column;
    width: var(--amai-chat-width, 100%);
    max-width: 100%;
    height: var(--amai-chat-height, 600px);
    max-height: 100%;
    overflow: hidden;
    overflow-anchor: none;
    background: var(--amai-bg);
    border: 1px solid var(--amai-border);
    border-radius: 12px;
    font-family: inherit;
    text-align: left;
}
/* Buttons don't take the font from their parent by themselves. */
.amai-chat-embed button { font-family: inherit; }
.amai-chat-embed:focus { outline: none; }
.amai-chat-embed:focus-visible,
.amai-chat-embed :focus-visible {
    outline: 2px solid var(--amai-primary);
    outline-offset: 2px;
}
.amai-chat-embed .amai-chat-input-field:focus,
.amai-chat-embed .amai-chat-inline-input-field:focus {
    box-shadow: 0 0 0 1px var(--amai-primary);
}
/* Light header: the window sits in the page, not over it. The bot name
 * and the AI line stay. No × in the page: there is nothing to close. */
.amai-chat-embed .amai-chat-header {
    background: var(--amai-bg);
    color: var(--amai-text);
    border-bottom: 1px solid var(--amai-border);
}
.amai-chat-embed .amai-chat-header-avatar {
    background: var(--amai-primary);
    color: #fff;
}
.amai-chat-embed .amai-chat-header-subtitle { color: var(--amai-muted); }
.amai-chat-embed .amai-chat-close {
    display: none;
    color: var(--amai-text);
}
.amai-chat-embed .amai-chat-close:hover { background: var(--amai-bot-bg); }
/* A short chat sits at the bottom, by the input, as in any messenger;
 * a long one scrolls as usual. */
.amai-chat-embed .amai-chat-messages { overscroll-behavior: contain; }
.amai-chat-embed .amai-chat-messages > :first-child { margin-top: auto; }
/* Compact before the visitor's chat begins: as tall as the greeting,
 * the suggestions and the input, or --amai-chat-idle-height, and
 * --amai-chat-idle-width wide (else its open width). The first message
 * grows it to --amai-chat-height and --amai-chat-width (embed.ts). The
 * list's base size is its content (flex: 1 alone is 0 in some browsers
 * when the box has no height of its own); with an idle height it fills
 * the box. */
.amai-chat-embed.amai-chat-embed-idle {
    height: var(--amai-chat-idle-height, auto);
    width: var(--amai-chat-idle-width, var(--amai-chat-width, 100%));
}
.amai-chat-embed-idle .amai-chat-messages { flex: 1 1 auto; }
/* --amai-chat-align: center or end puts a window narrower than its
 * container in the middle or at the end (embed.ts reads it: a keyword
 * in a variable can't set the margins by itself); start, as before, is
 * no class at all. */
.amai-chat-embed.amai-chat-embed-center { margin-inline: auto; }
.amai-chat-embed.amai-chat-embed-end { margin-inline-start: auto; }
/* The same window full-screen on a phone, on <body>, with its ×: the
 * page's width and alignment don't apply. */
.amai-chat-embed.amai-chat-embed-fullscreen {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: none;
    height: 100%;
    max-height: none;
    margin: 0;
    border: none;
    border-radius: 0;
    z-index: 2147483647;
}
.amai-chat-embed-fullscreen .amai-chat-close { display: block; }
@media (max-width: 480px) {
    .amai-chat-embed { height: var(--amai-chat-height, 70vh); }
    /* Below 16px, iOS zooms the page into a field on focus. */
    .amai-chat-embed .amai-chat-input-field,
    .amai-chat-embed .amai-chat-inline-input-field {
        font-size: 16px;
    }
}
@media (prefers-reduced-motion: reduce) {
    .amai-chat-embed * { transition: none; }
    .amai-chat-embed .amai-chat-typing span { animation: none; }
    .amai-chat-embed .amai-chat-send:hover,
    .amai-chat-embed .amai-chat-send:active,
    .amai-chat-embed .amai-chat-property-card-thumb:hover {
        transform: none;
    }
}
    `;
    document.head.appendChild(style);
}

function escapeCss(s: string): string {
    return s.replace(/[<>"'`\\]/g, "");
}
